/** Writing the day's list — the thing the working day actually starts with.
 *
 *  Two halves, and the top one is why this screen exists rather than just a repeated
 *  Add-task dialog. Before you type anything it shows **what is already coming to this
 *  day**: tasks that carried over from yesterday, and what the recurrence rules will
 *  generate at 06:00. Without that the obvious mistake is to type them again, and
 *  nothing downstream would catch the duplicate.
 *
 *  The bottom half is the grid for the genuinely new work. It posts once, and either
 *  every row lands or none does.
 */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState }
  from 'react'
import { useMutation, useQueries, useQuery, useQueryClient }
  from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { ApiError, api, upload, type Company, type DayPlan, type DraftImport,
         type ImportedRow, type OpenedDay, type Assignee, type RowProblem,
         type Task, type Workload } from '@/api/client'
import { AddTaskDrawer } from '@/components/AddTaskDrawer'
import { Banner, Empty, ErrorNote, Spinner, StatusChip, Tag, fmtDate,
         fmtDay, todayISO, useNarrow }
  from '@/components/ui'
import { WorkloadRail } from '@/components/WorkloadRail'
import { DocumentsField, documentsLabel, type DraftDocument }
  from '@/components/DocumentsField'
import { PersonCell, type Quorum } from '@/components/PeoplePicker'
import { CompanyCell } from '@/components/CompanyPicker'
import { Popover } from '@/components/Popover'
import { datedRows, fanOut, mergeImported, pickedDate } from './NewTasks.rows'
import { colsIn, inRect, moveCursor, normalizeRect, rectSize, tabNext, type Cell }
  from './NewTasks.grid'
import { GRID_COLUMNS, acceptCell, blankCell, copyCell, parseTsv, tsv }
  from './NewTasks.clip'
import { SignedOffConfirm, useSignedOffGate } from '@/components/SignedOffConfirm'
import { useAuth } from '@/lib/auth'

const PRIORITIES = ['Urgent', 'Important', 'Regular']
const BLANK = { task: '', company: '', responsible_ids: [] as string[],
                approver_ids: [] as string[],
                responsible_rule: 'ANY' as Quorum,
                approver_rule: 'ANY' as Quorum,
                priority: 'Regular', documents: [] as DraftDocument[],
                // Set it and the row runs from its Date to here: on the list every
                // day between, not carried forward until the window closes.
                end_date: '',
                // Blank means "the day at the top". A list is rarely all one date,
                // and before this the only way to date one row differently was to
                // file, change the picker and file again - so the empty default is
                // what keeps twenty rows for tomorrow a single edit.
                date: '',
                // Only on a row that came from a sheet: what would not resolve, and
                // why. Kept on the row rather than in a list beside the grid so the
                // reason sits where the row is.
                problems: [] as RowProblem[],
                // Reference documents for the doer, held in the browser until the
                // tasks exist - see `ReferenceCell`. Never sent in the JSON body;
                // `datedRows` strips it.
                reference: [] as File[] }
type Row = typeof BLANK

/** Chosen in the company cell to open the picker. Not a company name, so it cannot
 *  collide with one. */

/** The cap the API enforces (`BulkCreate`), mirrored so the grid refuses before the
 *  round trip rather than after it. */
const MAX_ROWS = 200

/** Which dates in use are not working days.
 *
 *  One request per DISTINCT date, not per row: a list of twenty rows has two or three
 *  dates between them, and `/days/{d}/plan` is the same endpoint the page-level panel
 *  already reads - so a date that is on screen anyway costs nothing.
 *
 *  It matters because the warning it drives is not cosmetic: carry-forward and the
 *  reminder jobs only run on working days, so a task dated on a Sunday is never moved
 *  on and never chased. That was said page-wide before there was anywhere else for a
 *  date to come from.
 */
function useOffDays(rows: Row[]): Set<string> {
  const dates = [...new Set(rows.map((r) => r.date).filter(Boolean))].sort()
  const answers = useQueries({
    queries: dates.map((d) => ({
      queryKey: ['day-plan', d],
      queryFn: () => api.get<DayPlan>(`/days/${d}/plan`),
    })),
  })
  return new Set(answers.flatMap(
    (a, i) => (a.data && !a.data.is_working_day ? [dates[i]] : [])))
}

/**
 *  The Task cell.
 *
 *  It was an <input>, which is a peephole: the text is kept in full but only the part
 *  the cursor is near is visible, so a description longer than the column could be
 *  typed and then never read back without clicking in and arrowing across. On a screen
 *  whose whole job is writing sentences that is the wrong control.
 *
 *  A textarea that grows with its content, to a ceiling of about five lines - past that
 *  it scrolls, so one person pasting an essay cannot push the next row off the screen.
 *
 *  Enter still files the row rather than inserting a newline: it is the documented way
 *  to reach the next row ("Tab across, Enter for the next row") and the habit somebody
 *  filling twenty rows builds in the first minute. Newlines are not wanted in a one-line
 *  description anyway - the wrapping is presentation, the stored value stays flat.
 */
const GROWN_MAX = 120
// The height of the selects beside it. A one-line box measures shorter than they
// do, and a Task cell visibly squatter than every control on its own row reads as
// broken rather than compact - so growth starts from their height, not from one
// line of text.
const ROW_H = 32

function TaskCell(props: {
  value: string
  placeholder?: string
  onChange: (v: string) => void
  onEnter: () => void
  /** Take the caret on mount. Row one only, and only once - see the effect below. */
  autoFocus?: boolean
}) {
  const ref = useRef<HTMLTextAreaElement>(null)

  // The page used to open with focus on `<body>`, which is 21 Tab presses from here:
  // fourteen rail links, Sign out, three theme swatches, the day picker and the
  // workload rail's toggle. Every time. So the caret starts where the work starts.
  //
  // On MOUNT only, with no dependency on the value - `rows` changes on every
  // keystroke, and re-running this would yank the caret out of whatever somebody is
  // typing several rows down.
  useEffect(() => {
    if (props.autoFocus) ref.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Layout effect, not effect: sizing after paint shows a one-line box for a frame and
  // then jumps. Keyed on the value, so only the row being edited re-measures.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    const wanted = el.scrollHeight
    el.style.height = Math.max(Math.min(wanted, GROWN_MAX), ROW_H) + 'px'
    el.style.overflowY = wanted > GROWN_MAX ? 'auto' : 'hidden'
  }, [props.value])

  return (
    <textarea ref={ref} rows={1}
              className="field min-w-[240px] resize-none leading-snug py-1.5"
              value={props.value} maxLength={2000}
              placeholder={props.placeholder}
              onChange={(e) => props.onChange(e.target.value)}
              onKeyDown={(e) => {
                // Bare Enter only. ⌘⏎ files the whole list and has to reach the
                // grid's handler - this used to `preventDefault` any Enter, so the
                // grid bailed on `defaultPrevented` and ⌘⏎ silently added a row
                // instead of filing.
                if (e.key !== 'Enter' || e.metaKey || e.ctrlKey || e.shiftKey) return
                e.preventDefault()
                props.onEnter()
              }} />
  )
}

/** The Documents cell: a summary, and the field in a popover over it.
 *
 *  Same shape as the Company and people cells, for the same reason: what a task must
 *  arrive with is decided while writing it, and making the Owner file twenty rows and
 *  then open twenty drawers is how the field goes unused.
 *
 *  Its own component so the trigger has a ref to measure, which is what `Popover`
 *  positions against. Wider than the pickers - a document list is names plus a
 *  required box per row, and 320px wraps every one of them.
 */
function DocsCell({ open, onOpen, value, onChange, onExit }: {
  open: boolean
  onOpen: (v: boolean) => void
  value: DraftDocument[]
  onChange: (docs: DraftDocument[]) => void
  /** Tab has run off the end of the panel - see `Popover`. */
  onExit?: (dir: 'forward' | 'back') => void
}) {
  const trigger = useRef<HTMLButtonElement>(null)
  return (
    <>
      <button ref={trigger} type="button"
              className="field text-left flex items-center gap-1.5"
              aria-expanded={open} onClick={() => onOpen(!open)}>
        <span className={value.length ? 'truncate' : 'truncate text-ink-3'}>
          {documentsLabel(value)}
        </span>
        <span className="ml-auto text-ink-3 text-[10px]">▾</span>
      </button>
      <Popover anchor={trigger.current} open={open} onClose={() => onOpen(false)}
               onExit={onExit} align="right" width={380}>
        <div className="p-3">
          <DocumentsField value={value} onChange={onChange}
                          onDone={() => onOpen(false)} />
        </div>
      </Popover>
    </>
  )
}

/** The largest file the store will take, mirrored so a too-big one is refused at the
 *  moment it is picked rather than after the tasks are already filed. */
const MAX_REFERENCE_BYTES = 10 * 1024 * 1024
/** The server's own per-task limit (`DEFAULT_MAX_PER_TASK`), mirrored so the grid and
 *  the store agree on the number rather than disagreeing by one. */
const MAX_REFERENCES = 10

/** How the cell reads when closed. Same shape as `documentsLabel` beside it. */
function referenceLabel(files: File[]): string {
  if (!files.length) return 'Attach…'
  if (files.length === 1) return files[0].name
  return `${files.length} files`
}

/** The reference documents for one row, in a panel.
 *
 *  Deliberately NOT part of the Documents cell beside it. Documents is a checklist of
 *  what the task must come back WITH - evidence the doer has to produce - and a
 *  reference is the opposite direction: what they are being given to start from.
 *  Filing a reference against a required document would also make it gate completion,
 *  which is the wrong meaning entirely.
 *
 *  Shaped like `DocumentsField`: a row per file, one button to add another, a `×`
 *  each, Escape to close. Two panels on one strip that behaved differently would be
 *  two things to learn.
 */
function ReferenceCell({ value, onChange, onExit }: {
  value: File[]
  onChange: (files: File[]) => void
  onExit?: (dir: 'forward' | 'back') => void
}) {
  const trigger = useRef<HTMLButtonElement>(null)
  const box = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [tooBig, setTooBig] = useState('')

  const close = () => {
    setOpen(false)
    setTooBig('')
    trigger.current?.focus()
  }

  return (
    <>
      <button ref={trigger} type="button"
              className="field text-left flex items-center gap-1.5"
              aria-haspopup="dialog" aria-expanded={open}
              aria-label={value.length
                ? `Reference: ${value.map((f) => f.name).join(', ')}`
                : 'Attach a reference'}
              onClick={() => setOpen(!open)}>
        <span className={value.length ? 'truncate' : 'truncate text-ink-3'}>
          {referenceLabel(value)}
        </span>
        <span className="ml-auto text-ink-3 text-[10px]">▾</span>
      </button>

      <Popover anchor={trigger.current} open={open} onClose={close}
               onExit={onExit} align="right" width={360}>
        <div className="p-3 space-y-1.5"
             onKeyDown={(e) => {
               if (e.key === 'Escape') { e.preventDefault(); close() }
             }}>
          {value.length === 0 && (
            <p className="text-[12px] text-ink-3">
              Nothing attached yet. Any file, up to 10 MB each.
            </p>
          )}
          {value.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-[12.5px]">
              <span className="truncate flex-1" title={f.name}>{f.name}</span>
              <span className="shrink-0 mono text-[11px] text-ink-3">
                {Math.max(1, Math.round(f.size / 1024))}k
              </span>
              <button type="button" aria-label={`Remove ${f.name}`}
                      className="text-ink-3 hover:text-rej-ink px-1 shrink-0"
                      onClick={() => onChange(value.filter((_, j) => j !== i))}>
                ×
              </button>
            </div>
          ))}

          {tooBig && <p className="text-[11.5px] text-rej-ink">{tooBig}</p>}

          <div className="flex items-center gap-2 pt-1">
            <button type="button" className="btn btn-sm"
                    disabled={value.length >= MAX_REFERENCES}
                    onClick={() => box.current?.click()}>
              {value.length ? 'Add another' : 'Choose a file'}
            </button>
            <span className="flex-1 text-[11px] text-ink-3">
              {value.length >= MAX_REFERENCES
                ? `${MAX_REFERENCES} is the limit`
                : `${value.length}/${MAX_REFERENCES}`}
            </span>
            <button type="button" className="btn btn-sm btn-primary" onClick={close}>
              Done
            </button>
          </div>
        </div>
      </Popover>

      {/* `multiple`, so several can be picked in one visit to the file dialog. */}
      <input ref={box} type="file" className="hidden" multiple
             onChange={(e) => {
               const picked = [...(e.target.files ?? [])]
               // Checked HERE, per file, not at filing time: the tasks are filed
               // before the uploads start, so learning about a size afterwards would
               // mean the work is saved and the reference is not.
               const big = picked.filter((f) => f.size > MAX_REFERENCE_BYTES)
               const ok = picked.filter((f) => f.size <= MAX_REFERENCE_BYTES)
               const room = MAX_REFERENCES - value.length
               setTooBig(
                 big.length
                   ? `${big.map((f) => f.name).join(', ')} — over the 10 MB limit.`
                   : ok.length > room
                     ? `Only ${room} more can be attached; the rest were left out.`
                     : '')
               if (ok.length) onChange([...value, ...ok.slice(0, room)])
               // Or picking the same file twice running fires no change event the
               // second time and the button looks broken.
               e.target.value = ''
             }} />
    </>
  )
}

/** The twenty-row grid is desk work, and says so on a phone.
 *
 *  It is a 1080px table of pickers: on 390px it is either a sideways scroll or a
 *  column of collapsed cells, and neither is a way to write a day's list. The rail
 *  entry and every "Write the day's list" button still land here, because a link
 *  that goes somewhere else depending on the width is a link nobody can share - so
 *  the screen arrives and offers the thing that does work at this size.
 *
 *  Not a redirect for the same reason: `/new` on a phone would silently become
 *  `/today`, and somebody who tapped it would conclude the button was broken.
 */
function OneAtATime() {
  const [adding, setAdding] = useState(false)
  return (
    <>
      <Empty
        title="Writing the whole list needs a wider screen"
        hint="The list grid is twenty rows of clients, people and approvers side by
              side - it is built for a desk. Adding tasks one at a time works
              properly here, and files exactly the same thing."
        action={
          <button className="btn btn-primary" onClick={() => setAdding(true)}>
            Add a task
          </button>
        } />
      {adding && <AddTaskDrawer onClose={() => setAdding(false)} />}
    </>
  )
}

export function NewTasks() {
  const phone = useNarrow()
  if (phone) return <OneAtATime />
  return <NewTasksGrid />
}

function NewTasksGrid() {
  const qc = useQueryClient()
  const nav = useNavigate()
  const { me } = useAuth()
  // Somebody who files only their own work has one possible answer to "who does it",
  // so the grid answers it for them rather than making them open a picker with a
  // single name in it, twenty rows in a row.
  const filesForSelfOnly = !!me?.may_enter_tasks && !me?.is_owner && !me?.is_manager
  const blankRow = (): Row => ({
    ...BLANK,
    responsible_ids: filesForSelfOnly && me ? [me.user_id] : [],
  })
  const [day, setDay] = useState(todayISO())
  const [rows, setRows] = useState<Row[]>(() => Array.from({ length: 5 }, blankRow))
  const [err, setErr] = useState<unknown>(null)
  // Minted once per grid, so a double-click or a retried request cannot file the
  // morning twice. `api.post` has always accepted this and nothing used it.
  const [key, setKey] = useState(() => crypto.randomUUID())
  // Which row is choosing companies, and what it has ticked so far.
  const [picking, setPicking] = useState<number | null>(null)
  // Which row's document list is open. Its own state rather than sharing
  // `picking`: they are different popovers and both can be reached from the
  // same row.
  const [docsOpen, setDocsOpen] = useState<number | null>(null)
  // One key for both people columns, so opening one closes the other - two
  // popovers over the same row would overlap.
  const [who, setWho] = useState<string | null>(null)
  const [picked, setPicked] = useState<string[]>([])
  /** The letter that opened a picker, handed to it as its starting query. Without it
   *  the first character somebody types on a cell is swallowed. */
  const [seed, setSeed] = useState('')
  // The cell cursor, and the other corner of a range. Both are set from ONE
  // focus handler on the table rather than tracked alongside it, so what is
  // highlighted and what has focus cannot disagree - see `onFocusCapture` below.
  const [cur, setCur] = useState<Cell | null>(null)
  const [anchor, setAnchor] = useState<Cell | null>(null)

  const plan = useQuery({
    queryKey: ['day-plan', day],
    queryFn: () => api.get<DayPlan>(`/days/${day}/plan`),
  })
  const offDays = useOffDays(rows)
  // Keyed identically to the rail's, so both read one request rather than two.
    // Only for somebody allowed to read reports. The workload numbers come from
    // `/reports/workload`, which needs APPROVER - so for a responsible person filing
    // their own work it answered 403 and painted an error banner across a screen that
    // was otherwise working. The bands are also close to meaningless for one person.
  const load = useQuery({
    queryKey: ['workload'],
    queryFn: () => api.get<Workload>('/reports/workload'),
    enabled: !!me?.permissions?.reports,
  })

  const companies = useQuery({
    queryKey: ['companies'], queryFn: () => api.get<Company[]>('/companies'),
  })
  const people = useQuery({
    // `/people`, not `/users`: scoped to the caller's own team, and without the
    // contact details. A manager is offered exactly the people the service will let
    // them file work onto, so the picker cannot suggest a choice that is then refused.
    // Keyed on the day, so the "signed off" marks belong to the day being filled in
    // rather than to whichever day was open first.
    queryKey: ['people', day], queryFn: () => api.get<Assignee[]>(`/people?day=${day}`),
  })

  // Sorted the way the Owner ordered them in Master data, so a fanned-out row comes
  // out in the firm's own company order rather than however the API returned them.
  const active = (companies.data ?? []).filter((c) => c.active)
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
  const staff = (people.data ?? []).filter((p) => p.active)
  const approvers = staff.filter((p) => p.roles.includes('APPROVER') || p.is_owner)
  // `/people` sends them their approvers as well as themselves, and offering those
  // as doers would suggest a row the service refuses.
  const doers = filesForSelfOnly
    ? staff.filter((p) => p.user_id === me?.user_id)
    : staff

  const filled = useMemo(() => rows.filter((r) => r.task.trim()), [rows])

  // ------------------------------------------------- out to Excel and back
  const [sheetErr, setSheetErr] = useState<unknown>(null)
  const [sheetBusy, setSheetBusy] = useState<'' | 'out' | 'in'>('')
  const [imported, setImported] = useState<DraftImport | null>(null)
  const [refused, setRefused] = useState(0)
  /** True while the reference files are going up, after the tasks have been filed.
   *  Its own flag, because at that point the mutation has already succeeded. */
  const [uploading, setUploading] = useState(false)
  const file = useRef<HTMLInputElement>(null)

  /** One imported row as a grid row.
   *
   *  The server has already resolved names to ids against the same eligibility
   *  functions the bulk post refuses by, so this is a shape change and nothing more.
   *  A cell it could not resolve arrives blank with its reason attached, which is
   *  what leaves the row visible, editable and unfilable until it is fixed.
   */
  const asRow = (r: ImportedRow): Row => ({
    ...blankRow(),
    task: r.task, company: r.company,
    responsible_ids: r.responsible_ids, approver_ids: r.approver_ids,
    responsible_rule: r.responsible_rule as Quorum,
    approver_rule: r.approver_rule as Quorum,
    priority: r.priority,
    documents: r.documents.map((d) => ({ name: d.name, required: d.required })),
    // `pickedDate`, so a sheet dated the same as the day at the top comes back on
    // the default rather than pinned to it - the same normalisation typing it in
    // would get.
    date: pickedDate(r.date, day), end_date: r.end_date,
    problems: r.problems,
  })

  const takeSheet = async (chosen: File) => {
    setSheetErr(null)
    setSheetBusy('in')
    try {
      const got = await upload<DraftImport>('/entry/import', chosen)
      const merged = mergeImported(rows, got.rows.map(asRow), MAX_ROWS, blankRow)
      setRows(merged.rows)
      setRefused(merged.refused)
      setImported(got)
      // A fresh key: these are different tasks from whatever the previous one was
      // minted for, and the idempotency key is what stops a retry filing twice.
      setKey(crypto.randomUUID())
    } catch (e) {
      setSheetErr(e)
    } finally {
      setSheetBusy('')
      // Or choosing the same file twice in a row fires no change event the second
      // time, and the button looks broken.
      if (file.current) file.current.value = ''
    }
  }

  // ------------------------------------------------- the grid as a sheet
  /** The rectangle currently selected, or null when the cursor is nowhere. */
  const rect = cur && anchor ? normalizeRect(anchor, cur) : null

  /** Whether a popover owns the keyboard. All three set their own state, and while
   *  any is open the arrow keys belong to it - moving the grid cursor underneath an
   *  open picker would scroll the row out from under it. */
  const inPopover = picking !== null || docsOpen !== null || who !== null

  /** The cell a DOM event happened in, from its data attributes.
   *
   *  Not `td[data-r]` any more: a row is two visual lines, and the second is a strip
   *  inside one `colSpan` cell, so its four cells are divs. The cursor cares that
   *  something is a CELL, not what tag it happens to be.
   */
  const cellOf = (target: EventTarget | null): Cell | null => {
    const el = (target as HTMLElement | null)?.closest?.('[data-cell]')
    if (!el) return null
    const r = Number(el.getAttribute('data-r'))
    const c = Number(el.getAttribute('data-c'))
    return Number.isNaN(r) || Number.isNaN(c) ? null : { r, c }
  }

  /** Whether the focus about to arrive is one this component asked for.
   *
   *  Load-bearing, and the reason is not obvious: `focusCell` moves focus, which
   *  fires the table's own focus handler, which sets the anchor - so a shift-arrow
   *  that had just extended a range had it collapsed again a microtask later, by the
   *  very move that was meant to grow it. Shift-selection could never span more than
   *  one cell, and a copy silently came out one cell wide.
   *
   *  A ref rather than state: it has to be readable inside the focus event that the
   *  same call stack triggers, before any re-render.
   */
  const asked = useRef(false)
  /** Whether the move about to land may open a picker. Off while shift-selecting: a
   *  range dragged across four empty cells is a selection, not four invitations to
   *  choose a client. */
  const mayOpen = useRef(true)
  /** The cell auto-open has already had its say about, as `r:c`.
   *
   *  Without it, closing a picker on an empty cell is a loop: the close returns focus
   *  to the cell, the cell is still empty, and it opens again. Remembering the cell
   *  means one offer per arrival - dismiss it and it stays dismissed until you go
   *  somewhere else and come back.
   */
  const offered = useRef('')

  /** Put the caret where the cursor is. `focus` on the `<td>` is NAV mode; on the
   *  cell's first control it is EDIT mode. */
  const focusCell = (at: Cell, edit: boolean, open = true) => {
    const td = document.querySelector<HTMLElement>(
      `[data-cell][data-r="${at.r}"][data-c="${at.c}"]`)
    if (!td) return
    asked.current = true
    mayOpen.current = open
    if (!edit) td.focus()
    else td.querySelector<HTMLElement>('textarea, input, select, button')?.focus()
    // Cleared on a timer as well as by the handler, so a move onto something with
    // nothing focusable in it cannot leave the flag stuck on.
    setTimeout(() => { asked.current = false; mayOpen.current = true }, 0)
  }

  // ------------------------------------------- a picker that opens on arrival
  /** Which picker an empty cell should offer, or '' if it should offer none.
   *
   *  The three that make up the spine of a row: which client, who does it, who
   *  signs it off. Documents is deliberately not here - it is optional on most
   *  tasks, so opening it on arrival would pop a panel on twenty rows that wanted
   *  nothing in it. Priority is a native select and always already has a value.
   *
   *  Only when the cell is EMPTY. Arriving at a cell that is already answered and
   *  having it spring open would fight somebody reading back what they typed.
   */
  /** Which picker a column holds, empty or not. */
  const pickerAt = (c: number): '' | 'company' | 'doer' | 'appr' =>
    c === 2 ? 'company' : c === 3 ? 'doer' : c === 4 ? 'appr' : ''

  const offerFor = (row: Row, c: number): '' | 'company' | 'doer' | 'appr' => {
    if (c === 2) return row.company ? '' : 'company'
    if (c === 3) return row.responsible_ids.length ? '' : 'doer'
    if (c === 4) return row.approver_ids.length ? '' : 'appr'
    return ''
  }

  /** Shut whatever is open. Company goes through `applyPick`, so ticks made and then
   *  arrowed away from are applied rather than thrown away - the ticks are the
   *  decision, exactly as when clicking away. */
  const closePickers = () => {
    if (picking !== null) applyPick()
    setWho(null)
    setDocsOpen(null)
    // Spent: the next picker to open was not opened by that letter.
    setSeed('')
  }

  /** Offer the cell's picker, once per arrival. Also what CLOSES the last one: every
   *  move through here either opens the new cell's picker or shuts the old one. */
  const offerPicker = (at: Cell, typed = '') => {
    const key = `${at.r}:${at.c}`
    // A typed letter always opens, even on a cell already offered and dismissed -
    // typing IS the request, so it is not the same thing as arriving here again.
    if (!typed && offered.current === key) return
    offered.current = key
    setSeed(typed)
    const row = rows[at.r]
    // A letter opens the cell whether or not it is empty: typing over a filled cell
    // to replace it is the spreadsheet gesture, and refusing would be surprising.
    const want = row && (typed || mayOpen.current)
      ? (typed ? pickerAt(at.c) : offerFor(row, at.c)) : ''
    if (!want) { closePickers(); return }
    if (want === 'company') {
      setWho(null)
      setDocsOpen(null)
      setPicking(at.r)
      setPicked(row.company ? [row.company] : [])
      return
    }
    if (picking !== null) applyPick()
    setDocsOpen(null)
    setWho(`${want === 'doer' ? 'doer' : 'appr'}:${at.r}`)
  }

  /** Tab has run off one end of an open panel.
   *
   *  Closing goes through `closePickers`, so Company applies its ticks exactly as
   *  Escape and clicking away already do - the ticks are the decision.
   *
   *  Forward lands on the next cell; backward lands on the cell the panel belongs to
   *  rather than the one before it. Shift+Tab out of a panel means "get me out of
   *  here", and putting the caret on the trigger you opened means the next Shift+Tab
   *  carries on leftward normally instead of silently skipping a cell.
   */
  const exitPopover = (at: Cell, dir: 'forward' | 'back') => {
    const bounds = { rows: rows.length,
                     cols: colsIn(!!rows[at.r]?.task.trim(), GRID_COLUMNS.length) }
    closePickers()
    const step = tabNext(at, bounds)
    // Forward off the very last cell makes a row, the same as a plain Tab there.
    if (dir === 'forward' && step.addRow && rows.length < MAX_ROWS) addRow()
    const next = dir === 'forward' ? step.cell : at
    setCur(next)
    setAnchor(next)
    // Belt and braces: landing in EDIT mode already means `onFocusCapture`'s
    // `[data-cell]` test fails and `offerPicker` cannot re-open the panel just
    // left, but priming the ref says so out loud.
    offered.current = `${next.r}:${next.c}`
    // A frame: `closePickers` unmounts the portal, and the Company path may fan out
    // and change how many rows there are.
    requestAnimationFrame(() => focusCell(next, true, false))
  }

  // Arrow keys inside an open picker belong to the PICKER - they walk its list, and
  // `CompanyPicker`/`PeoplePicker` handle them. The grid deliberately does not fight
  // for them: `inPopover` above makes `onGridKeyDown` stand down while one is open,
  // so Escape is the way back out to the cell.

  /** The cursor and range classes for one cell. */
  const cellClass = (r: number, c: number) => {
    if (!rect) return ''
    const on = cur?.r === r && cur?.c === c
    if (on) return ' cell-on'
    return rectSize(rect) > 1 && inRect(rect, r, c) ? ' cell-in-range' : ''
  }

  const onGridKeyDown = (e: React.KeyboardEvent<HTMLTableElement>) => {
    // A picker has already acted on this key - its own Enter takes the only match and
    // its Escape clears the query. Acting again here would do two things at once.
    if (e.defaultPrevented || inPopover) return
    if ((e.target as HTMLElement).closest('[data-popover]')) return

    const at = cellOf(e.target)
    if (!at) return
    // Per ROW, not per grid: a blank row renders only its first line, so its optional
    // columns do not exist to move into. See `colsIn`.
    const bounds = { rows: rows.length,
                     cols: colsIn(!!rows[at.r]?.task.trim(), GRID_COLUMNS.length) }
    // EDIT means a control has focus; NAV means the cell wrapper itself does. Tested
    // by `data-cell` rather than by tag, now that line 2's cells are divs.
    const editing = !(e.target as HTMLElement).matches?.('[data-cell]')
    const mod = e.metaKey || e.ctrlKey

    // Tab off the very last cell makes a row and goes into it, so a list grows by
    // typing rather than by reaching for "Add row". Everywhere else Tab is left
    // alone - it already walks the grid cell by cell, because each `<td>` holds one
    // control and document order is cell order.
    //
    // The spare-row effect and this cannot both fire: that effect only appends when
    // EVERY row has a task, and if it has just done so the row being typed in is no
    // longer last, so this branch does not match and the Tab is an ordinary native
    // move into the spare.
    if (e.key === 'Tab') {
      // From the CELL itself (NAV mode, after an Escape) the browser's next stop is
      // the control inside this very cell - so Tab appeared to do nothing and a
      // following Enter re-opened the picker you had just closed. Whenever the cursor
      // is on a `<td>`, Tab moves to the next cell rather than into its own child.
      if (!editing) {
        const step = e.shiftKey
          ? { cell: moveCursor(at, 'ArrowLeft', bounds), addRow: false }
          : tabNext(at, bounds)
        e.preventDefault()
        if (step.addRow) {
          if (rows.length >= MAX_ROWS) return
          addRow()
        }
        setCur(step.cell)
        setAnchor(step.cell)
        requestAnimationFrame(() => focusCell(step.cell, true, false))
        return
      }
      // From a control, Tab already walks the grid cell by cell on its own - each
      // `<td>` holds exactly one, so document order is cell order. The only place it
      // runs out is the very last cell, where focus escapes to the buttons below.
      if (e.shiftKey) return
      const step = tabNext(at, bounds)
      if (!step.addRow) return                    // let the browser do it
      e.preventDefault()
      if (rows.length >= MAX_ROWS) return         // at the cap, stay put
      addRow()
      setCur(step.cell)
      setAnchor(step.cell)
      requestAnimationFrame(() => focusCell(step.cell, true, false))
      return
    }

    // Escape leaves the control and takes the cursor: the one gesture that gets from
    // typing into a cell to moving between cells.
    if (e.key === 'Escape' && editing) {
      e.preventDefault()
      setCur(at); setAnchor(at)
      focusCell(at, false)
      return
    }

    // Bare Enter only: ⌘⏎ files the list, and this branch used to swallow it - it
    // matched any Enter with the cursor on a cell, so the shortcut just re-entered
    // the cell it was already on.
    if ((e.key === 'Enter' || e.key === 'F2') && !editing && !mod) {
      e.preventDefault()
      focusCell(at, true)
      return
    }

    // File the list from wherever you are. Without it, filing meant Tab through the
    // trailing blank row's eight controls and then three buttons - and "File N tasks"
    // is disabled until every filled row is complete, and a disabled button is not a
    // tab stop at all, so the target moved as you approached it.
    //
    // Through `gate.attempt()`, the same path the button takes, so the signed-off
    // confirmation still happens.
    if (mod && e.key === 'Enter') {
      e.preventDefault()
      if (save.isPending) return
      if (!ready) {
        // Say why rather than doing nothing: a shortcut that silently declines reads
        // as broken. The count beside the button says which rows.
        setErr(new Error(filled.length === 0
          ? 'Nothing to file yet - type a task first.'
          : `${problems.length} row${problems.length === 1 ? '' : 's'} still `
            + 'incomplete. Each one is marked below.'))
        return
      }
      setErr(null)
      void gate.attempt()
      return
    }

    // Fill down, the gesture a twenty-row list for one client is built on. Works in
    // either mode: it is about the SELECTION, not about the caret.
    if (mod && (e.key === 'd' || e.key === 'D')) {
      e.preventDefault()
      fillDown()
      return
    }

    // Select the whole grid. Only in NAV mode - inside a textarea Cmd+A still means
    // "select this text", which is what somebody rewriting a task description wants.
    if (mod && (e.key === 'a' || e.key === 'A') && !editing) {
      e.preventDefault()
      setAnchor({ r: 0, c: 0 })
      setCur({ r: rows.length - 1, c: GRID_COLUMNS.length - 1 })
      // Deliberately the full width even though the last row is usually blank: a
      // selection is about the DATA, and ⌘C reads the row object, not the DOM.
      return
    }

    // Remove the whole row. Its own key, because plain Delete/Backspace below already
    // means "empty these cells".
    //
    // Not Cmd+minus, which is the browser's zoom-out and cannot be intercepted
    // reliably. And it stands down inside a text field, where Cmd+Backspace natively
    // means "delete to the start of the line" - on the picker triggers, the select
    // and the date cells it has no native meaning, so it works there, which is what
    // keeps it usable on the Tab path without pressing Escape first.
    if (mod && (e.key === 'Backspace' || e.key === 'Delete')) {
      const el = e.target as HTMLElement
      const typing = el.tagName === 'TEXTAREA'
        || (el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'text')
      if (!typing) {
        e.preventDefault()
        removeRow(at.r)
        return
      }
    }

    // Empty the selection. NAV mode only, or Backspace would stop deleting
    // characters in the cell somebody is typing in.
    if ((e.key === 'Delete' || e.key === 'Backspace') && !editing) {
      e.preventDefault()
      clearSelection()
      return
    }

    // Typing over a cell replaces it, as in a spreadsheet. Only a bare printable key;
    // anything with a modifier is a shortcut.
    if (!editing && e.key.length === 1 && !mod && !e.altKey) {
      // A picker cell: open the list and hand it the letter. Focusing the trigger and
      // hoping was what happened before, and the trigger is a BUTTON - so the letter
      // went nowhere and no list opened.
      if (pickerAt(at.c)) {
        e.preventDefault()
        offerPicker(at, e.key)
        return
      }
      // Priority is a native `<select>`, whose type-ahead is its OWN default keydown
      // handling - and this event is targeted at the `<td>`, so focusing the select
      // does not replay the character and the first keystroke was silently lost. Do
      // it ourselves: three words, so the first letter is unambiguous.
      if (at.c === 6) {
        const want = PRIORITIES.find(
          (x) => x[0].toLowerCase() === e.key.toLowerCase())
        e.preventDefault()
        if (want) set(at.r, { priority: want })
        focusCell(at, true)
        return
      }
      // The date cells are left alone deliberately: a digit typed at a `<td>` is not
      // reliably replayed into a date input either, and Tab or Enter gets somebody in
      // cleanly, after which the browser's own date editing works untouched.
      if (at.c === 5 || at.c === 9) return
      focusCell(at, true)
      // Not prevented: the keypress lands in the control that just took focus.
      return
    }

    const arrow = e.key.startsWith('Arrow')
    // In a control the arrows belong to the control - a date input steps its value,
    // a select changes it, a textarea moves the caret. The exceptions are the
    // platform modifier, which is the typist's way out, and the Task box at either
    // end of its text, where up and down have nowhere left to go.
    let mayMove = !editing || mod
    if (arrow && editing && !mod) {
      const el = e.target as HTMLTextAreaElement
      const isTask = el.tagName === 'TEXTAREA'
      const collapsed = el.selectionStart === el.selectionEnd
      if (isTask && collapsed) {
        if (e.key === 'ArrowUp' && el.selectionStart === 0) mayMove = true
        if (e.key === 'ArrowDown' && el.selectionStart === el.value.length) {
          mayMove = true
        }
      }
    }

    const key = (e.key === 'Home' || e.key === 'End') && mod
      ? `Ctrl${e.key}` : e.key
    const handled = arrow || e.key === 'Home' || e.key === 'End'
    if (!handled || !mayMove) return

    const next = moveCursor(at, key, bounds)
    e.preventDefault()
    setCur(next)
    // Shift extends the selection; anything else starts a new one.
    if (!e.shiftKey) setAnchor(next)
    // A range dragged across empty cells is a selection, not an invitation to pick a
    // client in each of them.
    focusCell(next, editing && !!mod, !e.shiftKey)
  }

  /** Apply one patch per cell over a rectangle, collecting what would not resolve.
   *
   *  One path for paste, fill-down and clear, because all three are "change these
   *  cells" and the only difference is where the value comes from. It also means the
   *  three cannot disagree about what happens at the edge of the grid.
   */
  const overRect = (
      r0: number, r1: number, c0: number, c1: number,
      patchFor: (row: Row, r: number, c: number) => {
        patch: Partial<Row>
        problems?: RowProblem[]
      }) => {
    setRows((rs) => {
      const grown = [...rs]
      // Paste may run past the last row. Growing to fit is what a spreadsheet does,
      // and stopping at the cap is what the API demands.
      while (grown.length <= r1 && grown.length < MAX_ROWS) grown.push(blankRow())
      return grown.map((row, r) => {
        if (r < r0 || r > r1) return row
        let next = row
        let problems = row.problems
        for (let c = c0; c <= c1 && c < GRID_COLUMNS.length; c++) {
          const got = patchFor(next, r, c)
          const field = GRID_COLUMNS[c].key
          // A cell that now has a value has answered whatever was said about it.
          problems = problems.filter((p) => p.field !== field)
          if (got.problems?.length) problems = [...problems, ...got.problems]
          next = { ...next, ...got.patch }
        }
        return { ...next, problems }
      })
    })
  }

  /** Paste a block from a spreadsheet, anchored at the cursor.
   *
   *  Excel's own shape: the pasted block starts where the cursor is and runs right
   *  and down from it, growing the grid to fit rather than refusing. Names are
   *  matched against the same lists the pickers offer, so a paste cannot put an id in
   *  a cell the picker could not then name; anything unmatched leaves the cell alone
   *  and says so on the row, exactly as an imported sheet does.
   */
  const onGridPaste = (e: React.ClipboardEvent) => {
    if (inPopover || !cur) return
    if ((e.target as HTMLElement).closest('[data-popover]')) return
    const text = e.clipboardData.getData('text/plain')
    if (!text) return
    const cells = parseTsv(text)
    // A single cell with no tab or newline is ordinary typing into the focused
    // control - taking it over would break pasting a client's name into the Task box.
    // EDIT means a control has focus; NAV means the cell wrapper itself does. Tested
    // by `data-cell` rather than by tag, now that line 2's cells are divs.
    const editing = !(e.target as HTMLElement).matches?.('[data-cell]')
    if (editing && cells.length === 1 && cells[0].length === 1) return

    e.preventDefault()
    const ctx = { day, staff, companies: active }
    const r1 = Math.min(cur.r + cells.length - 1, MAX_ROWS - 1)
    const c1 = Math.min(cur.c + Math.max(...cells.map((x) => x.length)) - 1,
                        GRID_COLUMNS.length - 1)
    overRect(cur.r, r1, cur.c, c1, (_row, r, c) => {
      const said = cells[r - cur.r]?.[c - cur.c]
      if (said === undefined) return { patch: {} }
      const got = acceptCell(GRID_COLUMNS[c].key, said, ctx)
      return { patch: got.patch as Partial<Row>, problems: got.problems }
    })
    setCur({ r: cur.r, c: cur.c })
    setAnchor({ r: cur.r, c: cur.c })
  }

  /** Fill the selection down from its top row - Excel's Cmd+D.
   *
   *  The gesture this screen wants most: a day's list is often twenty rows for the
   *  same client with the same approver, and doing that by hand is twenty popovers.
   */
  const fillDown = () => {
    if (!rect || rect.r0 === rect.r1) return
    const top = rows[rect.r0]
    if (!top) return
    overRect(rect.r0 + 1, rect.r1, rect.c0, rect.c1,
             (_row, _r, c) => ({ patch: copyCell(top, GRID_COLUMNS[c].key) as
                                        Partial<Row> }))
  }

  /** Empty the selection - Delete or Backspace, as in any sheet. */
  const clearSelection = () => {
    if (!rect) return
    overRect(rect.r0, rect.r1, rect.c0, rect.c1,
             (_row, _r, c) => ({ patch: blankCell(GRID_COLUMNS[c].key) as
                                        Partial<Row> }))
  }

  /** Copy the selection as tab-separated text.
   *
   *  The `copy` event rather than a Cmd+C keydown, so Edit > Copy and the context
   *  menu work too - and `clipboardData.setData` is synchronous and needs neither a
   *  permission prompt nor a secure context, which `navigator.clipboard.writeText`
   *  does. This app is served over plain HTTP on a LAN, where that would fail with
   *  nothing said.
   */
  const onGridCopy = (e: React.ClipboardEvent) => {
    if (inPopover || !rect) return
    // One cell with text selected inside it is the browser's copy, not the grid's -
    // copying half a task description has to keep working.
    const nav = !!(e.target as HTMLElement).matches?.('[data-cell]')
    if (rectSize(rect) < 2 && !nav) return
    const text = tsv(rows, rect, { day, staff })
    if (!text) return
    e.preventDefault()
    e.clipboardData.setData('text/plain', text)
  }

  const set = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, n) => {
      if (n !== i) return r
      // Editing a cell clears the import's complaint about THAT cell. Otherwise
      // "no company called Nakoda Exprots" sits under a row whose company is now
      // right, which is a worse lie than saying nothing - the reader cannot tell a
      // stale note from a live one.
      const touched = Object.keys(patch)
      const fields = new Set(touched.flatMap((k) => (
        k === 'responsible_ids' || k === 'responsible_rule' ? ['responsible']
        : k === 'approver_ids' || k === 'approver_rule' ? ['approver']
        : [k])))
      const problems = r.problems.filter((p) => !fields.has(p.field))
      return { ...r, ...patch, problems }
    }))

  /** Picking a company fills the approver from that company's default — the field
   *  Master data has always captured and nothing ever read. Overridable. */
  const addRow = () =>
    setRows((rs) => (rs.length >= MAX_ROWS ? rs : [...rs, blankRow()]))

  /** Drop a row.
   *
   *  No append logic here, deliberately: the spare-row effect below adds a blank
   *  whenever EVERY row has a task, so after a removal either a blank still exists or
   *  every survivor is filled and the effect supplies one. Removing the last row
   *  leaves an empty list, and `[].every(...)` is true, so a blank appears there too.
   *  "You can never end up with nowhere to type" stays one rule rather than two.
   */
  const removeRow = (i: number) => {
    setRows((rs) => rs.filter((_, n) => n !== i))
    // The selection must not outlive the row it pointed at.
    setCur((c) => (c ? { r: Math.max(0, Math.min(c.r, rows.length - 2)), c: c.c }
                     : c))
    setAnchor(null)
    // Land on the row that slid up into its place, or the one above if this was the
    // last - otherwise focus falls to `<body>` and the keyboard path dies mid-list.
    const land = { r: Math.max(0, Math.min(i, rows.length - 2)), c: cur?.c ?? 1 }
    requestAnimationFrame(() => focusCell(land, true, false))
  }

  /** The approver a company would suggest, or '' — one place, so a fanned-out row and
   *  a hand-picked one cannot disagree.
   *
   *  Only ever somebody this filer is actually offered. The company default is usually
   *  a partner, and for somebody filing their own work that person may not be on their
   *  approver ladder at all - so suggesting them put an id in the cell that the picker
   *  could not name and the service would then refuse. Where exactly one approver is
   *  on offer - which is the case when an owner has named one - that is the answer
   *  whatever the company says.
   */
  const approverFor = (name: string) => {
    const offered = approvers.map((a) => a.user_id)
    if (offered.length === 1) return offered[0]
    const co = active.find((c) => c.name === name)
    const suggested = co?.default_approver_user_id ?? ''
    return offered.includes(suggested) ? suggested : ''
  }

  /** The same suggestion, spelled out. Two helpers rather than one, because the row
   *  stores an id and the company list shows a person - and printing the id there
   *  turned "Nakoda Exports  Priya Nair" into "Nakoda Exports  U-002". */
  const approverNameFor = (name: string) => {
    const id = approverFor(name)
    return id ? (staff.find((p) => p.user_id === id)?.name ?? '') : ''
  }

  /** Replace the row being picked with one row per company.
   *
   *  Real rows rather than a hidden list on one row: each keeps **its own** company's
   *  default approver, which a single approver field cannot express, and each stays
   *  editable before anything is filed. It also keeps the 200 cap honest and keeps
   *  the server's "Row 3" refusals pointing at a row you can actually see.
   */
  const expand = () => {
    if (picking === null) return
    const at = picking
    // In the firm's own company order, not tick order: `active` is sorted by
    // `sort_order`, and `fanOut` keeps whatever order it is handed.
    const chosen = active.filter((c) => picked.includes(c.name)).map((c) => c.name)
    setRows((rs) => {
      const next = fanOut(rs, at, chosen, approverFor, MAX_ROWS)
      if (!next) return rs
      // Picking a company is how an imported row's company gets FIXED, and it does
      // not go through `set` - so the clearing there never saw it, and "no company
      // called Nakoda Exprots" stayed under a row whose company was now right. The
      // fan-out fills the approver from the company default too, so that complaint
      // is answered here as well.
      const answered = new Set(['company', 'approver'])
      return next.map((r, i) => (
        i >= at && i < at + chosen.length
          ? { ...r, problems: r.problems.filter((p) => !answered.has(p.field)) }
          : r))
    })
    setPicking(null)
    setPicked([])
  }

  /** Close the picker, keeping what was ticked.
   *
   *  One tick and ten go through the same path: `expand` replaces the row with one
   *  per chosen company, which for a single company is just setting it. Treating them
   *  as one operation is the point - the old modal made "several" a separate act.
   */
  const applyPick = () => {
    if (picked.length && !tooMany) { expand(); return }
    setPicking(null)
    setPicked([])
  }

  const wouldBe = picking === null ? 0 : rows.length - 1 + picked.length
  const tooMany = wouldBe > MAX_ROWS
  // Every active company ticked means *all* - including any client added later, which
  // is the distinction worth keeping (see the Working calendar's ALL handling).
  const allPicked = active.length > 0 && picked.length === active.length

  // Always keep one spare row, so there is never a moment where you have to reach for
  // a button before typing the next task.
  useEffect(() => {
    if (rows.length < MAX_ROWS && rows.every((r) => r.task.trim())) addRow()
  }, [rows])

  // Armed, not applied. Choosing "Work this day" only opens the day when a task is
  // actually filed, so an Owner who changes their mind and types nothing leaves the
  // calendar exactly as they found it. Re-armed per date - see the effect below.
  const [armed, setArmed] = useState(false)
  const [opened, setOpened] = useState<OpenedDay | null>(null)
  useEffect(() => { setArmed(false); setOpened(null) }, [day])

  const save = useMutation({
    mutationFn: (acknowledge: string[] = []) => api.post<Task[]>('/tasks/bulk',
      { tasks: datedRows(rows, day),
        acknowledge_closed: acknowledge }, key),
    onSuccess: async (made) => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['day'] })
      qc.invalidateQueries({ queryKey: ['day-plan'] })
      // The rail's saved half, so the rows just filed stop being counted as drafts.
      qc.invalidateQueries({ queryKey: ['workload'] })

      // ---- the references, now that the tasks they belong to exist ----
      //
      // This cannot happen before the post: an attachment row needs a task_id, and
      // there is no staging endpoint to park bytes against. So the bulk post is
      // atomic and these uploads are not, which is a real seam and is reported as
      // one rather than smoothed over.
      // Flattened to one entry per FILE, not per row: a row may carry several, and
      // each is its own upload that can fail on its own.
      const refs = filled.flatMap((r, n) =>
        r.reference.map((file) => ({ file, task: made[n], row: n + 1 })))
      if (refs.length) {
        // Positional mapping is only sound on the fresh path: `create_bulk` builds
        // its result in the order it was sent and is all-or-nothing. The
        // idempotency-replay branch re-reads by ref and filters by visibility, so a
        // length mismatch means the rows and the tasks may not line up - and
        // attaching a reference to the WRONG task is the one outcome that must be
        // impossible.
        if (made.length !== filled.length) {
          setErr(new Error(
            `The ${refs.length} task${refs.length === 1 ? '' : 's'} filed, but the `
            + 'reference files were not attached - this looks like a repeated '
            + 'submission, and matching them to the right tasks is no longer safe. '
            + 'Attach them from the tasks themselves.'))
          return
        }
        setUploading(true)
        const failed: string[] = []
        for (const r of refs) {
          try {
            await upload(`/tasks/${r.task.task_id}/attachments`, r.file)
          } catch {
            // Named by row AND by file: with several on one row, "row 3 failed" does
            // not say which of its three files is missing.
            failed.push(`row ${r.row} · ${r.file.name}`)
          }
        }
        setUploading(false)
        if (failed.length) {
          // The tasks are filed and cannot be taken back, so this says exactly that
          // rather than looking like the filing failed.
          setErr(new Error(
            `All ${made.length} task${made.length === 1 ? '' : 's'} were filed, but `
            + `${failed.length} reference file${failed.length === 1 ? '' : 's'} did `
            + `not upload (${failed.join('; ')}). The task${
              made.length === 1 ? ' is' : 's are'} safe - attach ${
              failed.length === 1 ? 'it' : 'them'} from the task, or try again.`))
          return
        }
      }

      if (armed && !p.is_working_day) {
        // Stay put and report it. Navigating away would leave the Owner to work out
        // for themselves that the calendar changed and the backlog moved.
        try {
          setOpened(await api.post<OpenedDay>(`/days/${day}/open`, {}))
          qc.invalidateQueries({ queryKey: ['calendar'] })
          qc.invalidateQueries({ queryKey: ['holidays'] })
          return
        } catch (e) {
          // The tasks are filed either way; only the calendar change failed, and
          // saying so is better than a screen that silently did half of it.
          setErr(e)
          return
        }
      }
      nav('/today', { state: { added: made.length } })
    },
    onError: (e) => {
      // A sign-off is a question the dialog is about to ask, not a failure. An error
      // banner beside it would say two contradictory things at once.
      if (e instanceof ApiError && e.isPersonDayClosed) return
      setErr(e)
    },
  })

  // One confirmation for the whole submit. Warning about one person, being confirmed,
  // then warning about the next is three round trips to file one list.
  const gate = useSignedOffGate((ack) => save.mutateAsync(ack))

  // Mirrors the server's own row check, so an obvious mistake is caught in the row it
  // belongs to rather than as a sentence about row nine.
  const rowProblem = (r: Row) =>
    !r.task.trim() ? null
      : !r.company ? 'needs a company'
      : !r.responsible_ids.length ? 'needs somebody to do it'
      // Reachable by changing the day at the top, not by typing: moving it forward
      // past a row's Until leaves the end before the start. The browser's `min` on
      // that input updates but does not re-check a value already chosen, so without
      // this the row is only refused at submit, as "Row 3: End Date is before Start
      // Date" - a sentence about a row number rather than a mark on the row.
      : r.end_date && r.end_date < (r.date || day) ? 'ends before it starts'
      : null
  const problems = filled.map(rowProblem).filter(Boolean) as string[]
  const ready = filled.length > 0 && problems.length === 0

  if (plan.isLoading || companies.isLoading || people.isLoading) {
    return <Spinner label="Reading the day" />
  }
  if (plan.error) return <ErrorNote error={plan.error} />

  const p = plan.data!
  const carried = p.existing.filter((t) => t.carried)
  const raised = p.existing.filter((t) => !t.carried)

  return (
    <>
      {gate.asking && (
        <SignedOffConfirm people={gate.asking} busy={save.isPending}
                          onCancel={gate.cancel}
                          onConfirm={() => { void gate.confirm() }} />
      )}
      {err ? <ErrorNote error={err} /> : null}
      {sheetErr ? <ErrorNote error={sheetErr} /> : null}
      {imported && (
        <Banner tone={imported.rows.some((r) => r.problems.length)
                        || refused > 0 || imported.truncated ? 'warn' : 'info'}>
          {/* Every number, because the arithmetic is what tells somebody whether
              their sheet arrived whole. A count that does not add up is the first
              sign a row went missing. */}
          <b>{imported.read} row{imported.read === 1 ? '' : 's'} read.</b>{' '}
          {(() => {
            const bad = imported.rows.filter((r) => r.problems.length).length
            const good = imported.rows.length - bad
            const said = [`${good} came in cleanly`]
            if (bad) said.push(`${bad} need a look`)
            if (imported.skipped.length) {
              said.push(imported.skipped.length === 1
                ? '1 row was not a task'
                : `${imported.skipped.length} rows were not tasks`)
            }
            if (refused) said.push(`${refused} did not fit`)
            return said.join(' · ')
          })()}
          {imported.truncated && (
            <span className="block mt-1">
              The sheet was longer than the {MAX_ROWS} that can be filed at once —
              file these, then upload it again for the rest.
            </span>
          )}
          {imported.rows.some((r) => r.problems.length) && (
            <span className="block mt-1">
              Nothing was dropped. The rows that need a look are marked below, with
              the cell left blank — fix them and they file with the rest.
            </span>
          )}
          {' '}
          <button className="underline" onClick={() => setImported(null)}>
            Hide this
          </button>
        </Banner>
      )}

      <div className="card mb-4 p-4">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <label className="label">The day you are filling</label>
            {/* `min` is today: a task cannot be raised for a day whose carry-forward
                has already run. The server refuses it too — this is so you never get
                that far. */}
            <input className="field" type="date" value={day} min={todayISO()}
                   onChange={(e) => { setDay(e.target.value); setKey(crypto.randomUUID()) }} />
          </div>
          <div className="text-[12.5px] text-ink-2 pb-1.5">
            <b>{fmtDate(day)}</b>
            {day === todayISO() && <span className="ml-2 text-ink-3">today</span>}
            {/* Said here because the Date column is the last one and easy to miss,
                and because this field's meaning changed: it is the default now, not
                the date. */}
            <span className="block text-[11.8px] text-ink-3 mt-0.5">
              Every row goes here unless its own Date says otherwise.
            </span>
          </div>
        </div>

        {!p.is_working_day && !opened && (
          <div className="mt-3 flex items-center gap-3 rounded-md bg-pend-tint
                          px-3 py-2.5 text-[12.5px] text-pend-ink">
            <span>
              {armed ? (
                <>
                  <b>{fmtDate(day)} will become a working day</b> when you file this
                  list. Work still outstanding is brought onto it, and the day sends
                  its digest and reminders like any other.
                </>
              ) : (
                <>
                  <b>{fmtDate(day)} is not a working day.</b> A task dated here is
                  never moved on and never chased — unless the firm is working it.
                </>
              )}
            </span>
            <span className="flex-1" />
            {/* Turning a holiday into a working day is `POST /days/{day}/open`, which
                is owner_only - and it is armed to run *after* the tasks are filed, so
                for anybody else the work saved and the calendar half answered 403.
                Filing on the day still works for everyone; only the calendar change
                is withheld, and it was never theirs to make. */}
            {!me?.is_owner ? null : armed ? (
              <button className="btn btn-sm shrink-0" onClick={() => setArmed(false)}>
                Leave it a holiday
              </button>
            ) : (
              <>
                <button className="btn btn-sm shrink-0"
                        onClick={() => setArmed(true)}>
                  Work this day
                </button>
                {p.next_working_day && (
                  <button className="btn btn-sm shrink-0"
                          onClick={() => {
                            setDay(p.next_working_day!)
                            setKey(crypto.randomUUID())
                          }}>
                    Use {fmtDate(p.next_working_day)}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {p.calendar_warning && (
          <p className="mt-3 text-[12px] text-pend-ink">
            <b>{p.calendar_warning}</b> Past that date the app treats holidays as
            ordinary working days, so work can be carried onto one.
          </p>
        )}
      </div>

      {opened && (
        <Banner tone="info">
          <b>{fmtDate(opened.day)} is now a working day.</b>{' '}
          {opened.will_carry_in
            ? 'Tonight\u2019s carry-forward will bring outstanding work onto it, and it '
              + 'sends its digest and reminders like any other working day.'
            : opened.moved.length
              ? `${opened.moved.length} outstanding task${
                  opened.moved.length === 1 ? '' : 's'} moved onto it, and everyone `
                + 'affected has been told.'
              : 'There was no outstanding work to bring onto it.'}{' '}
          <Link to="/today" className="underline">Open today’s list</Link>.
        </Banner>
      )}

      {p.is_closed && (
        <Banner tone="bad">
          {/* Said "You can still file against it" until now - true when written, and
              false since `_guard_target_day_open` landed. Filing onto a closed day is
              a 409, so the old wording invited a submit that could only fail. */}
          <b>This day is closed.</b> Nothing more can be filed onto it — its counters
          have already been signed off. Reopen the day first, or date this work for
          another day.
        </Banner>
      )}

      {p.invalid_rules.length > 0 && (
        <Banner tone="warn">
          {p.invalid_rules.length} recurring{' '}
          {p.invalid_rules.length === 1 ? 'rule is' : 'rules are'} invalid and will
          generate nothing — check the <b>Recurring</b> screen.
        </Banner>
      )}

      {/* ------------------------------------------------------------- the new work */}
      {/* The rail sits above the grid rather than beside it. As a column it was costing
          the Task field - the only cell with free text in it - about three quarters of
          its width. */}
      {/* The band and the grid share a containing block so `sticky` is bounded by the
          grid. Without it the band follows the scroll all the way down and hangs over
          "Already coming to this day", where it answers a question nobody is asking. */}
      <div className="relative">
      {/* The rail runs its own `/reports/workload` query, which needs APPROVER.
          Rendering it for a responsible person filing their own work painted a 403
          banner across an otherwise working screen - and the bands say little about
          one person anyway. */}
      {me?.permissions?.reports ? <WorkloadRail rows={filled} /> : null}
      <div className="card">
        <div className="card-head">
          <b className="text-[13px]">New tasks for this day</b>
          <span className="text-[11.8px] text-ink-3">
            Tab through every cell · in a list, arrows walk it and space ticks, Esc
            closes · ⌘⏎ files · ⌘⌫ removes a row · ⌘C/⌘V · ⌘D fills down
          </span>
          {/* The "already coming" list used to sit above this and be read on the way
              down. It is below now, so its whole point - do not type these again -
              needs saying where the typing happens. A count and a way to get there. */}
          {p.existing.length + p.from_rules.length > 0 && (
            <a href="#already" className="text-[11.8px] text-ink-3 underline">
              {p.existing.length + p.from_rules.length} already on this day
            </a>
          )}
          <span className="flex-1" />
          <span className="mono text-[11.5px] text-ink-3">
            {filled.length} to add
          </span>
        </div>

        {/* `grid-scroll`, not `table-wrap`: a sticky header needs a box that really
            scrolls vertically, and `table-wrap` has no height. The `min-w` moves onto
            the table here, because `.table-wrap > table` no longer applies. */}
        <div className="grid-scroll">
          {/* The six fixed columns below come to 832px. The floor is that plus a
              usable Task field, and it is not lifted at lg: dropping it there is
              exactly what let Task collapse to a single character once the rail took
              288px off the page. Scrolling sideways is a nuisance; a 68px field you
              have to type a sentence into is not usable at all. */}
          {/* One focus handler for the whole table, so clicks, Tab and the arrow
              keys all feed ONE cursor and nothing has to be kept in step. */}
          <table className="w-full border-collapse min-w-[1000px] grid-tight"
                 onKeyDown={onGridKeyDown} onCopy={onGridCopy}
                 onPaste={onGridPaste}
                 onFocusCapture={(e) => {
                   const at = cellOf(e.target)
                   if (!at) return
                   // A move this component asked for has already set the cursor, and
                   // the anchor is deliberately left where it was - see `asked`.
                   if (asked.current) asked.current = false
                   else {
                     setCur(at)
                     // Tabbing or clicking into a cell starts a new selection; only
                     // shift-arrow and shift-click extend one.
                     setAnchor(at)
                   }
                   // Both Tab and the arrows open an empty picker cell, so there is
                   // one habit rather than two: Tab lands on the cell's trigger
                   // BUTTON, the arrows land on the `<td>` itself, and either counts
                   // as arriving. `mayOpen` still suppresses it while shift-selecting,
                   // and `focusCell(..., false)` suppresses it on the way OUT of a
                   // panel, which is what stops the two looping.
                   const el = e.target as HTMLElement
                   if (el.matches?.('[data-cell]') || el.tagName === 'BUTTON') {
                     offerPicker(at)
                   }
                 }}
                 onMouseDownCapture={(e) => {
                   if (!e.shiftKey) return
                   const at = cellOf(e.target)
                   if (!at) return
                   // Stop focus entering the control: a shift-click on the Company
                   // cell would otherwise pop the picker open mid-selection.
                   e.preventDefault()
                   setCur(at)
                   // And drop the browser's own text highlight, which would
                   // otherwise fight the cell highlight and hand `copy` a range.
                   window.getSelection()?.removeAllRanges()
                 }}>
            {/* Six columns, not ten. Priority, Documents, Reference and Until moved
                to a second line per row: ten columns needed about 1500px against
                1200 of usable width, so the grid scrolled sideways and Task - the one
                field holding a sentence - was squeezed to its 240px floor. These six
                come to 752px fixed, which leaves Task about 500px. */}
            <thead><tr>
              <th className="th th-sticky w-8"></th>
              <th className="th th-sticky">Task</th>
              <th className="th th-sticky w-52">Company</th>
              <th className="th th-sticky w-44">Who does it</th>
              <th className="th th-sticky w-44">Who approves</th>
              <th className="th th-sticky w-36">Date</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => {
                const problem = rowProblem(r)
                return (
                  // Two `<tr>`s per row, so the key goes on a Fragment around them.
                  <Fragment key={i}>
                  <tr className={`group/row${
                        problem ? ' bg-pend-tint/30' : ''}`}>
                    <td data-cell data-r={i} data-c={0} tabIndex={-1}
                        className={`td mono text-[11.5px] text-ink-3 relative${
                          cellClass(i, 0)}`}>
                      {i + 1}
                      {/* `tabIndex={-1}` on purpose: Tab has to visit every CELL, and
                          a focusable × would add a stop per row before the first Task
                          box ever came up. The mouse gets the button, the keyboard
                          gets ⌘⌫. Shown on hover or when the row has focus, so a
                          twenty-row grid is not littered with crosses. */}
                      <button type="button" tabIndex={-1}
                              aria-label={`Remove row ${i + 1}`}
                              title="Remove this row"
                              onClick={() => removeRow(i)}
                              className="absolute right-0.5 top-1/2 -translate-y-1/2
                                         px-1 text-ink-3 opacity-0 hover:text-rej-ink
                                         group-hover/row:opacity-100
                                         focus-visible:opacity-100 transition-opacity">
                        ×
                      </button>
                    </td>
                    <td data-cell data-r={i} data-c={1} tabIndex={-1} className={`td${cellClass(i, 1)}`}>
                      {/* min-w on the input, not `w-*` on the <th>: under
                          table-layout:auto a column width is advisory, and an empty
                          text input collapses to almost nothing while the company
                          picker beside it holds its size. On desktop there was slack
                          so it never showed. */}
                      <TaskCell
                        value={r.task}
                        autoFocus={i === 0}
                        placeholder={i === 0 ? 'File GSTR-3B for August 2026' : ''}
                        onChange={(v) => set(i, { task: v })}
                        onEnter={() => {
                          addRow()
                          // "Enter for the next row" has been on the card head since
                          // it was written, and only ever added a row - the caret
                          // stayed put, so the next row had to be clicked. The row
                          // may not exist yet, so this waits a frame for it.
                          const next = { r: i + 1, c: 1 }
                          requestAnimationFrame(() => focusCell(next, true))
                        }} />
                      {problem && (
                        <span className="block text-[11.5px] text-pend-ink mt-1">
                          {problem}
                        </span>
                      )}
                      {/* Why the cell is empty, under what is missing from it. The
                          import problem EXPLAINS the live one: "no company called
                          Nakoda Exprots" above "needs a company". Its own line, and
                          its own row number, because that number is the
                          spreadsheet's - it points at something still open in
                          Excel. */}
                      {r.problems.map((p, n) => (
                        <span key={n}
                              className="block text-[11.5px] text-rej-ink mt-0.5">
                          {p.message}
                          {p.did_you_mean ? ` Did you mean ${p.did_you_mean}?` : ''}
                        </span>
                      ))}
                    </td>
                    {/* A native <select> cannot hold checkboxes, and routing "several"
                        through a modal made picking two companies feel like a different,
                        heavier act than picking one. It is the same act. The list opens
                        in place, ticking is ticking, and one tick or ten differ only in
                        how many rows come back.

                        The picker itself is shared with the drawer, the edit form and
                        the recurring editor now - only the footer below is this
                        screen's, because only this screen turns ticks into rows. */}
                    <td data-cell data-r={i} data-c={2} tabIndex={-1} className={`td${cellClass(i, 2)}`}>
                      <CompanyCell
                        open={picking === i}
                        onOpen={(v) => {
                          if (!v) {
                            applyPick()
                            // Back to the cell, not nowhere. Picking closes the
                            // panel, and without this the caret was left in a
                            // portal that had just unmounted - so the next arrow
                            // key went to the page instead of the grid.
                            focusCell({ r: i, c: 2 }, false, false)
                            return
                          }
                          setPicking(i)
                          setPicked(r.company ? [r.company] : [])
                        }}
                        companies={active} multiple
                        onExit={(dir) => exitPopover({ r: i, c: 2 }, dir)}
                        initialQuery={picking === i ? seed : ''}
                        // While open the ticks are a DRAFT: applying them is a
                        // fan-out, not a set, so the row's own company is only the
                        // starting point.
                        value={picking === i ? picked : (r.company ? [r.company] : [])}
                        onChange={setPicked}
                        summary={(ns) => (ns.length > 1
                          ? `${ns.length} companies` : '')}
                        hint={(c) => approverNameFor(c.name)}
                        footer={
                          <div className="px-3 py-2 border-t border-rule">
                            {tooMany ? (
                              <p className="text-[11.5px] text-rej-ink">
                                That would make {wouldBe} rows; the most that can be
                                filed at once is {MAX_ROWS}. Untick some, or file
                                these and come back.
                              </p>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="text-[11.5px] text-ink-3 flex-1">
                                  {picked.length === 0 ? 'Nothing ticked'
                                    : picked.length === 1 ? 'One task'
                                    : `${picked.length} rows, one per company`}
                                </span>
                                <button className="btn btn-sm btn-primary"
                                        onClick={applyPick}>Done</button>
                              </div>
                            )}
                            {allPicked && !tooMany ? (
                              <p className="text-[11px] text-ink-3 mt-1.5">
                                Archived companies are never included — archiving is
                                what takes a client out of new-task dropdowns.
                              </p>
                            ) : null}
                          </div>
                        } />
                    </td>
                    <td data-cell data-r={i} data-c={3} tabIndex={-1} className={`td${cellClass(i, 3)}`}>
                      <PersonCell
                        open={who === `doer:${i}`}
                        onOpen={(v) => {
                          setWho(v ? `doer:${i}` : null)
                          if (!v) focusCell({ r: i, c: 3 }, false, false)
                        }}
                        staff={doers} load={load.data} kind="doer"
                        onExit={(dir) => exitPopover({ r: i, c: 3 }, dir)}
                        initialQuery={who === `doer:${i}` ? seed : ''}
                        label={`Who does row ${i + 1}`}
                        ids={r.responsible_ids} rule={r.responsible_rule}
                        onChange={(ids, rule) =>
                          set(i, { responsible_ids: ids, responsible_rule: rule })} />
                    </td>
                    <td data-cell data-r={i} data-c={4} tabIndex={-1} className={`td${cellClass(i, 4)}`}>
                      <PersonCell
                        open={who === `appr:${i}`}
                        onOpen={(v) => {
                          setWho(v ? `appr:${i}` : null)
                          if (!v) focusCell({ r: i, c: 4 }, false, false)
                        }}
                        staff={approvers} load={load.data} kind="approver"
                        onExit={(dir) => exitPopover({ r: i, c: 4 }, dir)}
                        initialQuery={who === `appr:${i}` ? seed : ''}
                        label={`Who approves row ${i + 1}`}
                        placeholder="none"
                        ids={r.approver_ids} rule={r.approver_rule}
                        onChange={(ids, rule) =>
                          set(i, { approver_ids: ids, approver_rule: rule })} />
                    </td>

                    {/* The EFFECTIVE date, not the stored one. A row with no date of
                        its own is filed on the day at the top, and showing that
                        rather than an empty cell is the difference between a column
                        that says what will happen and one that reads as unset.
                        Because `day` is read at render rather than copied into row
                        state, every row still on the default follows the picker at
                        the top the moment it changes - no syncing, no effect.
                        `pickedDate` decides what that means for storage. `min` is
                        today, mirroring `check_not_backdated` so the browser refuses
                        what the server would.

                        Date closes line one: it is one of the five fields a row
                        always has, so it belongs beside them rather than in the
                        optional strip. */}
                    <td data-cell data-r={i} data-c={5} tabIndex={-1} className={`td${cellClass(i, 5)}`}>
                      <input type="date" className="field" value={r.date || day}
                             min={todayISO()} aria-label={`Date for row ${i + 1}`}
                             onChange={(e) => set(
                               i, { date: pickedDate(e.target.value, day) })} />
                      {r.date && r.date !== day && (
                        <span className="block text-[11px] text-ink-3 mt-0.5">
                          {fmtDay(r.date)}
                          {/* The page-level holiday panel only knows about the page
                              date, so a row moved onto a Sunday would otherwise get
                              none of that warning - and a task dated on a
                              non-working day is never moved on and never chased. */}
                          {offDays.has(r.date) && (
                            <span className="text-pend-ink"> · not a working day</span>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>

                  {/* Line two: the quiet strip.
                      Rendered only once the row has a task. A row that will not be
                      filed needs no priority and no run-until date, and twenty blank
                      rows of them would double the height of an empty grid to say
                      nothing. `colsIn` is what stops the cursor walking into cells
                      that are not here.

                      One `colSpan` cell holding four of its own, because a table
                      forces one shared column grid on every row - taking these out of
                      it is the only way to size them independently of line one. Each
                      carries `data-cell`, which is how `cellOf` still finds them. */}
                  {r.task.trim() ? (
                    <tr className={problem ? 'bg-pend-tint/30' : undefined}>
                      <td className="td py-1" colSpan={6}>
                        <div className="flex items-center gap-x-5 gap-y-1 flex-wrap
                                        pl-[3.1rem]">
                          <label className="flex items-center gap-1.5
                                            text-[11.5px] text-ink-3">
                            <span className="whitespace-nowrap">Priority</span>
                            <div data-cell data-r={i} data-c={6} tabIndex={-1} className={`w-36${cellClass(i, 6)}`}>
                      <select className="field" value={r.priority}
                              aria-label={`Priority for row ${i + 1}`}
                              onChange={(e) => set(i, { priority: e.target.value })}>
                        {PRIORITIES.map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                    </div>
                          </label>
                          <label className="flex items-center gap-1.5
                                            text-[11.5px] text-ink-3">
                            <span className="whitespace-nowrap">Must arrive with</span>
                            <div data-cell data-r={i} data-c={7} tabIndex={-1} className={`relative w-36${cellClass(i, 7)}`}>
                      <DocsCell
                        open={docsOpen === i}
                        onOpen={(v) => {
                          setDocsOpen(v ? i : null)
                          // The one cell that never restored focus, so pressing Done
                          // unmounted the focused button and dropped the caret on
                          // `<body>` - arrows dead, next Tab back at the top of the
                          // page. The other three have always done this.
                          if (!v) focusCell({ r: i, c: 7 }, false, false)
                        }}
                        onExit={(dir) => exitPopover({ r: i, c: 7 }, dir)}
                        value={r.documents}
                        onChange={(docs) => set(i, { documents: docs })} />
                    </div>
                          </label>
                          <label className="flex items-center gap-1.5
                                            text-[11.5px] text-ink-3">
                            <span className="whitespace-nowrap">Reference</span>
                            <div data-cell data-r={i} data-c={8} tabIndex={-1}
                        className={`w-36${cellClass(i, 8)}`}>
                      <ReferenceCell
                        value={r.reference ?? null}
                        onChange={(f) => set(i, { reference: f })} />
                    </div>
                          </label>
                          <label className="flex items-center gap-1.5
                                            text-[11.5px] text-ink-3">
                            <span className="whitespace-nowrap">Runs until</span>
                            <div data-cell data-r={i} data-c={9} tabIndex={-1} className={`w-36${cellClass(i, 9)}`}>
                      <input type="date" className="field" value={r.end_date}
                             min={r.date || day}
                             aria-label={`Runs until, row ${i + 1}`}
                             onChange={(e) => set(i, { end_date: e.target.value })} />
                      {r.end_date && (
                        <span className="block text-[11px] text-ink-3 mt-0.5">
                          runs {fmtDay(r.date || day)} → {fmtDay(r.end_date)}
                        </span>
                      )}
                    </div>
                          </label>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-rule flex items-center gap-2
                        flex-wrap">
          <button className="btn btn-sm" onClick={addRow}
                  disabled={rows.length >= MAX_ROWS}>
            Add row
          </button>
          {/* The round trip. The workbook carries the firm's own clients and people
              as real Excel dropdowns, so a list drafted away from the app comes back
              with the names already matching. */}
          <button className="btn btn-sm" disabled={!!sheetBusy}
                  onClick={async () => {
                    setSheetErr(null)
                    setSheetBusy('out')
                    try {
                      await api.download(
                        `/entry/template.xlsx?day=${encodeURIComponent(day)}`)
                    } catch (e) { setSheetErr(e) } finally { setSheetBusy('') }
                  }}>
            {sheetBusy === 'out' ? 'Preparing…' : 'Download for Excel'}
          </button>
          <button className="btn btn-sm" disabled={!!sheetBusy}
                  onClick={() => file.current?.click()}>
            {sheetBusy === 'in' ? 'Reading…' : 'Upload a filled sheet'}
          </button>
          <input ref={file} type="file" className="hidden"
                 accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                 onChange={(e) => {
                   const chosen = e.target.files?.[0]
                   if (chosen) void takeSheet(chosen)
                 }} />
          <span className="flex-1" />
          {problems.length > 0 && (
            <span className="text-[12px] text-pend-ink">
              {problems.length} row{problems.length === 1 ? '' : 's'} incomplete
            </span>
          )}
          <button className="btn btn-primary"
                  disabled={!ready || save.isPending || uploading}
                  onClick={() => { setErr(null); void gate.attempt() }}>
            {save.isPending ? 'Filing…'
              : uploading ? 'Attaching…'
              : `File ${filled.length} task${filled.length === 1 ? '' : 's'}`}
          </button>
        </div>
        <p className="px-4 pb-3 text-[11.8px] text-ink-3">
          Filed in one go: if any row is refused, <b>none</b> of them are saved and no
          reference numbers are used up. Nothing here is written until you press the
          button.
        </p>
      </div>

      </div>

      {/* ---------------------------------------- what is already coming to this day */}
      {(p.existing.length > 0 || p.from_rules.length > 0) ? (
        <div className="card mb-4" id="already">
          <div className="card-head">
            <b className="text-[13px]">Already coming to this day</b>
            <span className="text-[11.8px] text-ink-3">
              Do not type these again — they are here, or they will be
            </span>
            <span className="flex-1" />
            <span className="mono text-[11.5px] text-ink-3">
              {p.existing.length + p.from_rules.length}
            </span>
          </div>

          {carried.length > 0 && (
            <>
              <div className="px-4 pt-3 pb-1.5 text-2xs font-semibold uppercase
                              tracking-wider text-ink-3">
                Carried over — {carried.length}
              </div>
              <div className="table-wrap">
                <table className="w-full border-collapse">
                  <tbody>
                    {carried.map((t) => (
                      <tr key={t.ref}>
                        <td className="td w-16 mono text-[12.5px]">#{t.ref}</td>
                        <td className="td">{t.task}
                          <span className="block text-[11.5px] text-ink-3">
                            {t.company} · {t.responsible || 'nobody yet'}
                          </span>
                        </td>
                        <td className="td w-40">
                          <Tag tone="bad">carried {t.carry_forward_count}×</Tag>
                        </td>
                        <td className="td w-40"><StatusChip status={t.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="px-4 py-2 text-[11.8px] text-ink-3">
                These moved here because they were not finished. Nothing to do on this
                screen — they are already on the list.
              </p>
            </>
          )}

          {raised.length > 0 && (
            <>
              <div className="px-4 pt-3 pb-1.5 text-2xs font-semibold uppercase
                              tracking-wider text-ink-3">
                Already raised for this day — {raised.length}
              </div>
              <div className="table-wrap">
                <table className="w-full border-collapse">
                  <tbody>
                    {raised.map((t) => (
                      <tr key={t.ref}>
                        <td className="td w-16 mono text-[12.5px]">#{t.ref}</td>
                        <td className="td">{t.task}
                          <span className="block text-[11.5px] text-ink-3">
                            {t.company} · {t.responsible || 'nobody yet'}
                          </span>
                        </td>
                        <td className="td w-40">
                          {t.from_rule ? <Tag>from a rule</Tag> : null}
                        </td>
                        <td className="td w-40"><StatusChip status={t.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {p.from_rules.length > 0 && (
            <>
              <div className="px-4 pt-3 pb-1.5 text-2xs font-semibold uppercase
                              tracking-wider text-ink-3">
                Recurring rules will add — {p.from_rules.length}
              </div>
              <div className="table-wrap">
                <table className="w-full border-collapse">
                  <tbody>
                    {p.from_rules.map((r) => (
                      <tr key={r.rule_ref + r.occurrence}>
                        <td className="td w-16 mono text-[12.5px]">{r.rule_ref}</td>
                        <td className="td">{r.task}</td>
                        <td className="td w-56 text-[12px] text-ink-3">
                          {r.moved
                            ? `due ${fmtDate(r.occurrence)}, moved to this day`
                            : 'due on this day'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="px-4 py-2 text-[11.8px] text-ink-3">
                These do not exist yet — the 06:00 job creates them, or{' '}
                <b>Generate due tasks now</b> on the Recurring screen. Typing them here
                would give the firm two of each.
              </p>
            </>
          )}
        </div>
      ) : (
        <Banner tone="info">
          Nothing is on this day yet — no carried-over work and no recurring rule
          due, so everything you file above is new.
        </Banner>
      )}


    </>
  )
}
